import React, { useState, createContext, useContext, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';

/**
 * Promise-based dialog that asks whether a print is a "real" print (recorded in statistics) or just a preview.
 * Mirrors confirmation-modal.ctx but resolves to one of three outcomes:
 *   'record'  -> print for real, count it in statistics
 *   'preview' -> generate the PDF only, do not record
 *   null      -> cancelled / dismissed
 *
 * Usage:
 *   const { showPrintIntent } = usePrintIntent();
 *   const choice = await showPrintIntent({ message }); // optional title/message/recordText/previewText overrides
 *   if (!choice) return;
 *   const recordPrint = choice === 'record';
 */
const PrintIntentModalContext = createContext({});

export const PrintIntentChoice = {
  RECORD: 'record',
  PREVIEW: 'preview',
};

const PrintIntentModalProvider = ({ children }) => {
  const { t } = useTranslation();
  const resolver = useRef();

  const [open, setOpen] = useState(false);
  const [content, setContent] = useState({});

  const showPrintIntent = (opts = {}) => {
    setContent({
      title: opts.title || t('printIntent.title'),
      message: opts.message || t('printIntent.message'),
      recordText: opts.recordText || t('printIntent.record'),
      previewText: opts.previewText || t('printIntent.preview'),
    });
    setOpen(true);
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  };

  const onDecision = (choice) => {
    setOpen(false);
    resolver.current && resolver.current(choice);
  };

  const value = { showPrintIntent };

  return (
    <PrintIntentModalContext.Provider value={value}>
      {children}
      <Dialog open={open} onClose={() => onDecision(null)} fullWidth maxWidth="xs">
        {content.title && <DialogTitle>{content.title}</DialogTitle>}
        <DialogContent>
          <DialogContentText component="div">{content.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => onDecision(null)}>
            {t('common.cancel')}
          </Button>
          <Button variant="outlined" onClick={() => onDecision(PrintIntentChoice.PREVIEW)}>
            {content.previewText}
          </Button>
          <Button variant="contained" onClick={() => onDecision(PrintIntentChoice.RECORD)} autoFocus>
            {content.recordText}
          </Button>
        </DialogActions>
      </Dialog>
    </PrintIntentModalContext.Provider>
  );
};

export default PrintIntentModalProvider;

export const usePrintIntent = () => useContext(PrintIntentModalContext);
